import React, { useCallback, memo } from 'react'
import { Spin, Empty } from 'antd'
import { useNavigate } from 'react-router-dom'
import { useEntityRelations } from './hooks/useEntityRelations'
import { RelationsContainer, RelationsGrid, LoadingContainer } from './styles'
import { RelationCardView } from './relation-card-view'
import { formatPhoneNumber } from '../../../lib/utils/helpers'
import { NotesCell } from '../v2/notes-cell'
import { EntityRelationsProps, RelationEntity } from './interfaces'

const EntityRelations: React.FC<EntityRelationsProps> = memo(
  ({ entityType, entityId, className }) => {
    const { relations, loading, error } = useEntityRelations({ entityType, entityId })
    const navigate = useNavigate()
    const handleEntityClick = useCallback(
      (entity: RelationEntity) => {
        switch (entity.type) {
          case 'appeal':
            navigate(`/appeal/${entity.id}`)
            break
          case 'order':
            navigate(`/order/${entity.id}`)
            break
          case 'measurement':
            navigate(`/measurements/${entity.id}`)
            break
          case 'montage':
            navigate(`/montage/${entity.id}`)
            break
          case 'claim':
            navigate(`/claim/${entity.parent_id}`)
            break
          case 'claim-request':
            navigate(`/claim-request/${entity.id}`)
            break
          case 'claim-work':
            navigate(`/claim-work/${entity.id}`)
            break
          case 'delivery':
            navigate(`/logistics/deliveries/${entity.id}`)
            break
          case 'employees':
            navigate(`/hr/${entity.id}`)
            break
          default:
            break
        }
      },
      [navigate],
    )

    const getNoteCardId = useCallback((entity: RelationEntity) => {
      switch (entity.type) {
        case 'appeal':
          return entity.id
        case 'order':
        case 'measurement':
        case 'montage':
        case 'claim':
          return entity.id
        case 'delivery':
          return entity.id
        default:
          return entity.id
      }
    }, [])

    const getNoteCardType = useCallback((entity: RelationEntity): 'case' | 'delivery' => {
      return entity.type === 'delivery' ? 'delivery' : 'case'
    }, [])

    const formatDate = useCallback((dateString?: string) => {
      if (!dateString) return null
      try {
        return new Date(dateString).toLocaleDateString('ru-RU', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      } catch {
        return dateString
      }
    }, [])

    const formatDateTime = useCallback((dateString?: string) => {
      if (!dateString) return null
      try {
        return new Date(dateString).toLocaleString('ru-RU', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      } catch {
        return dateString
      }
    }, [])

    const renderEntityInfo = useCallback(
      (entity: RelationEntity) => {
        const info: Array<{ label: string; value: string | null }> = []

        // Общие поля
        if (entity.address) {
          info.push({ label: 'Адрес:', value: entity.address })
        }
        if (entity.manager_name) {
          info.push({ label: 'Менеджер:', value: entity.manager_name })
        }
        if (entity.customer_phone) {
          info.push({ label: 'Телефон клиента:', value: formatPhoneNumber(entity.customer_phone) })
        }
        if (entity?.info?.length) {
          entity.info.map(b => info.push(b))
        }

        switch (entity.type) {
          case 'order':
            break

          case 'measurement':
            if (entity.measurement_date_plan) {
              const date = formatDate(entity.measurement_date_plan)
              info.push({
                label: 'Дата и время замера (план):',
                value: `${date} ${entity.measurement_time_plan}`,
              })
            }
            if (entity.measurement_date_fact) {
              info.push({
                label: 'Дата и время замера (факт):',
                value: formatDateTime(entity.measurement_date_fact),
              })
            }
            break

          case 'montage':
            if (entity.montage_date_plan) {
              info.push({
                label: 'Дата и время монтажа (план):',
                value: `${formatDate(entity.montage_date_plan)} ${entity.montage_time_plan}`,
              })
            }
            if (entity.montage_date_fact) {
              info.push({
                label: 'Дата и время монтажа (факт):',
                value: formatDateTime(entity.montage_date_fact),
              })
            }
            break

          case 'claim':
            if (entity.claim_text) {
              info.push({ label: 'Суть претензии:', value: entity.claim_text })
            }
            if (entity.compensation_responsible_name) {
              info.push({
                label: 'Ответственный за ущерб:',
                value: entity.compensation_responsible_name,
              })
            }
            if (entity.claim_request_created_at) {
              info.push({
                label: 'Дата заявки:',
                value: formatDate(entity.claim_request_created_at),
              })
            }
            break

          case 'claim-request':
            if (entity.claim_text) {
              info.push({ label: 'Суть претензии:', value: entity.claim_text })
            }
            if (entity.compensation_responsible_name) {
              info.push({
                label: 'Ответственный за ущерб:',
                value: entity.compensation_responsible_name,
              })
            }
            if (entity.claim_request_created_at) {
              info.push({
                label: 'Дата заявки:',
                value: formatDate(entity.claim_request_created_at),
              })
            }
            break

          case 'delivery':
            if (entity.delivery_date_plan) {
              info.push({
                label: 'Дата доставки (план):',
                value: formatDate(entity.delivery_date_plan),
              })
            }
            if (entity.floor_elevator) {
              info.push({
                label: 'Комментарий:',
                value: entity.floor_elevator,
              })
            }
            break

          case 'employees':
            info.push({
              label: 'Дата отклика:',
              value: formatDate(entity.created_at),
            })
            break
        }

        return info.filter(item => item.value)
      },
      [formatDate, formatDateTime],
    )

    if (loading) {
      return (
        <LoadingContainer>
          <Spin size="large" />
        </LoadingContainer>
      )
    }

    if (error) {
      return (
        <RelationsContainer className={className}>
          <Empty description={error} />
        </RelationsContainer>
      )
    }

    if (relations.length === 0) {
      return (
        <RelationsContainer className={className}>
          <Empty description="Связанные сущности не найдены" />
        </RelationsContainer>
      )
    }

    return (
      <RelationsContainer className={className}>
        <RelationsGrid>
          {relations.map(entity => (
            <RelationCardView
              key={`${entity.type}-${entity.id}`}
              title={entity.title}
              onClick={() => handleEntityClick(entity)}
              stage={entity.stage}
              info={renderEntityInfo(entity)}
              headerContent={
                entity.notes_count !== undefined && entity.notes_count > 0 ? (
                  <div onClick={event => event.stopPropagation()}>
                    <NotesCell
                      cardId={getNoteCardId(entity)}
                      count={entity.notes_count}
                      cardType={getNoteCardType(entity)}
                    />
                  </div>
                ) : null
              }
            />
          ))}
        </RelationsGrid>
      </RelationsContainer>
    )
  },
)

EntityRelations.displayName = 'EntityRelations'

export default EntityRelations
